/**
 * Permissions — Tier B (departmental) gating helpers.
 *
 * Tier A:  role (admin > engineer/editor > viewer)
 * Tier B:  departments[] — which department(s) a user belongs to
 *
 * A button is accessible when ANY of:
 *   • user.role === 'admin'                          (Tier A bypass)
 *   • button is universal (not in BUTTON_DEPTS)
 *   • user has any department listed in BUTTON_DEPTS[button]
 *   • user has a non-expired GRANT for that button (temporary access)
 *
 * KEEP IN SYNC with /app/backend/permissions_routes.py BUTTON_DEPTS.
 */

export const DEPARTMENTS = [
  { code: 'spa',             label: 'Spa' },
  { code: 'fb',              label: 'F&B' },
  { code: 'rooms',           label: 'Rooms' },
  { code: 'finance',         label: 'Finance' },
  { code: 'sales',           label: 'Sales' },
  { code: 'engineering',     label: 'Engineering' },
  { code: 'entertainment',   label: 'Entertainment' },
  { code: 'water-sports',    label: 'Water Sports' },
  { code: 'hr',              label: 'HR' },
  { code: 'it',              label: 'IT' },
  { code: 'security',        label: 'Security' },
  { code: 'contractor',      label: 'Contractor' },
  { code: 'procurement',     label: 'Procurement' },
];

export const BUTTON_DEPTS = {
  // Department workspace tiles
  'ws-spa':            ['spa'],
  'ws-fb':             ['fb'],
  'ws-rooms':          ['rooms'],
  'ws-finance':        ['finance'],
  'ws-sales':          ['sales'],
  'ws-engineering':    ['engineering'],
  'ws-grounds':        ['engineering'],
  'ws-entertainment':  ['entertainment'],
  'ws-water-sports':   ['water-sports'],
  'ws-hr':             ['hr'],
  'ws-it':             ['it'],
  'ws-security':       ['security'],
  'ws-procurement':    ['procurement'],
  'ws-storage':        ['procurement'],
  // Main pill buttons that are NOT universal
  'property-budgets':         ['finance', 'procurement'],
  'property-technical-plans': ['finance', 'contractor'],
  'subcontractors':           ['finance', 'contractor'],
  'tldi':                     ['finance', 'contractor', 'engineering'],
  'insurer-assets':           ['contractor'],
  'property-permits':         ['finance', 'contractor'],
  // ws-brand, ws-laundry, ws-ose, virtual-tour, designs, render-center,
  // project-search, ai-credentials, tutorial — UNIVERSAL (absent here).
};


/* iter-89 — Six roles total. `ownership` and `top_leader` carry FULL
 * admin permissions for UI/CMS access but are silenced from automated
 * outbound messages — that filtering happens server-side. Anywhere
 * the UI used to gate on `role === 'admin'`, it should now use
 * `isAdminLike(role)` so the new roles inherit the same access.
 *
 * iter-97 — `engineer` is now the HIGHEST permission tier (matches the
 * backend `ADMIN_LIKE_ROLES` constant). Engineers pass every admin-level
 * gate and can also be the only role allowed to modify other engineers
 * (use `isSuperRole()` for that narrower check).
 */
export const ADMIN_LIKE_ROLES = ['engineer', 'admin', 'ownership', 'top_leader'];
export const SUPER_ROLES = ['engineer'];
export const SILENCED_ROLES = ['ownership', 'top_leader'];

export function isAdminLike(role) {
  return ADMIN_LIKE_ROLES.includes((role || '').toLowerCase());
}

/** Strictly engineer — used to gate the few actions only the top tier
 * can take (e.g. demoting another engineer / admin). iter-97. */
export function isSuperRole(role) {
  return SUPER_ROLES.includes((role || '').toLowerCase());
}

/** True when this role/user must NEVER be sent automated outbound msgs. */
export function isSilencedRole(role) {
  return SILENCED_ROLES.includes((role || '').toLowerCase());
}


/**
 * Does the user have visibility on this button?
 *   role         — 'admin' | 'editor' | 'engineer' | 'viewer' | 'ownership' | 'top_leader'
 *   departments  — array of department codes the user belongs to
 *   grants       — array of {button, granted_until ISO} active grants
 *   button       — the button key (testid-style, e.g. 'ws-spa')
 *
 * iter-97: `engineer` is now part of ADMIN_LIKE_ROLES so `isAdminLike()`
 * already covers the bypass. (Previously iter-96 added an explicit
 * engineer check here when engineer was a lower tier — no longer needed.)
 */
export function hasButtonAccess(role, departments, grants, button) {
  if (isAdminLike(role)) return true;
  const required = BUTTON_DEPTS[button];
  if (!required || required.length === 0) return true;            // universal
  const userDepts = new Set((departments || []).map((d) => String(d).toLowerCase()));
  if (required.some((d) => userDepts.has(d))) return true;
  // Active grant?
  const now = Date.now();
  return (grants || []).some((g) => {
    if (g.button !== button) return false;
    if (!g.granted_until) return false;
    return new Date(g.granted_until).getTime() > now;
  });
}

/** Human-readable department labels keyed by code, for tooltips. */
const _DEPT_LABEL_BY_CODE = DEPARTMENTS.reduce((m, d) => { m[d.code] = d.label; return m; }, {});

/** Build a "Visible to: Admins · Engineers · F&B · Procurement" string
 * for any button, suitable for hover tooltips on padlocks. iter-96. */
export function accessHintForButton(button) {
  const required = BUTTON_DEPTS[button];
  if (!required || required.length === 0) {
    return 'Visible to: everyone signed in';
  }
  const deptLabels = required.map((d) => _DEPT_LABEL_BY_CODE[d] || d);
  return `Visible to: Admins · Engineers · ${deptLabels.join(' · ')}`;
}

/** Get a human-readable list of which departments unlock this button. */
export function deptsForButton(button) {
  return BUTTON_DEPTS[button] || [];
}

/** Look up a department label by code (e.g. 'fb' → 'F&B'). */
export function deptLabel(code) {
  const m = DEPARTMENTS.find((d) => d.code === code);
  return m ? m.label : code;
}


/* iter-119 — Canonical catalog of every dashboard/user-access button
 * that admins/engineers can disable. `id` is the key used by canAccess
 * and the backend toggle row. `testid` is the actual data-testid value
 * rendered in the DOM (used by the App.js CSS-injection effect to apply
 * the greyed-out/hidden state without touching every JSX block). KEEP
 * IN SYNC with the JSX in /app/frontend/src/App.js. */
export const DASHBOARD_BUTTONS = [
  // Primary CTAs / hero region
  { id: 'tutorial-top',             testid: 'tutorial-top-btn',         label: 'Take the Tutorial',           category: 'Hero' },
  { id: 'virtual-tour',             testid: 'virtual-tour-btn',         label: 'Start Virtual Tour',          category: 'Hero' },
  // Property data buttons
  { id: 'property-technical-plans', testid: 'technical-plans-btn',      label: 'Property Technical Plans',    category: 'Property' },
  { id: 'property-budgets',         testid: 'property-budgets-btn',     label: 'Property Budgets',            category: 'Property' },
  { id: 'ws-designs',               testid: 'ws-designs',               label: 'Designs & Visualisations',    category: 'Property' },
  { id: 'render-center',            testid: 'render-center-btn',        label: 'Render Center',               category: 'Property' },
  { id: 'insurer-assets',           testid: 'insurer-assets-btn',       label: 'Insurer Assets · Disposal',   category: 'Property' },
  { id: 'emergency-response',       testid: 'emergency-response-btn',   label: 'Emergency Response',          category: 'Property' },
  { id: 'post-hurricane-damage',    testid: 'post-hurricane-damage-btn',label: 'Post Hurricane Damage',       category: 'Property' },
  { id: 'npr-reports',              testid: 'npr-reports-btn',          label: 'NPR Reports / Initiatives',   category: 'Property' },
  { id: 'reports-articles',         testid: 'reports-articles-btn',     label: 'Reports / Articles',          category: 'Property' },
  { id: 'audits',                   testid: 'audits-btn',               label: 'Audits',                      category: 'Property' },
  { id: 'safety',                   testid: 'safety-btn',               label: 'Safety',                      category: 'Property' },
  { id: 'property-permits',         testid: 'property-permits-btn',     label: 'Licenses & Permits',          category: 'Property' },
  { id: 'project-search',           testid: 'project-search-btn',       label: 'Project Search',              category: 'Property' },
  { id: 'subcontractors',           testid: 'subcontractors-btn',       label: 'Subcontractors',              category: 'Property' },
  { id: 'tldi',                     testid: 'tldi-btn',                 label: 'TLDI',                        category: 'Property' },
  { id: 'ai-credentials',           testid: 'ai-credentials-btn',       label: 'AI Credentials',              category: 'Property' },
  { id: 'sops',                     testid: 'sops-btn',                 label: 'SOPs',                        category: 'Property' },
  { id: 'replay-tutorial',          testid: 'replay-tutorial-btn',      label: 'Replay Tutorial',             category: 'Property' },
  // Departmental workspaces
  { id: 'ws-it',                    testid: 'ws-it',                    label: 'IT/Systems',                  category: 'Workspaces' },
  { id: 'ws-hr',                    testid: 'ws-hr',                    label: 'HR',                          category: 'Workspaces' },
  { id: 'ws-engineering',           testid: 'ws-engineering',           label: 'Engineering',                 category: 'Workspaces' },
  { id: 'ws-finance',               testid: 'ws-finance',               label: 'Finance',                     category: 'Workspaces' },
  { id: 'ws-brand',                 testid: 'ws-brand',                 label: 'Brand Standards',             category: 'Workspaces' },
  { id: 'ws-grounds',               testid: 'ws-grounds',               label: 'Grounds',                     category: 'Workspaces' },
  { id: 'ws-procurement',           testid: 'ws-procurement',           label: 'Procurement Planning',        category: 'Workspaces' },
  { id: 'ws-fb',                    testid: 'ws-fb',                    label: 'F&B',                         category: 'Workspaces' },
  { id: 'ws-rooms',                 testid: 'ws-rooms',                 label: 'Rooms',                       category: 'Workspaces' },
  { id: 'ws-spa',                   testid: 'ws-spa',                   label: 'Spa',                         category: 'Workspaces' },
  { id: 'ws-security',              testid: 'ws-security',              label: 'Security',                    category: 'Workspaces' },
  { id: 'ws-entertainment',         testid: 'ws-entertainment',         label: 'Entertainment',               category: 'Workspaces' },
  { id: 'ws-water-sports',          testid: 'ws-water-sports',          label: 'Water Sports',                category: 'Workspaces' },
  { id: 'ws-sales',                 testid: 'ws-sales',                 label: 'Sales',                       category: 'Workspaces' },
  { id: 'ws-laundry',               testid: 'ws-laundry',               label: 'Laundry',                     category: 'Workspaces' },
  { id: 'ws-ose',                   testid: 'ws-ose',                   label: 'OS&E Lists',                  category: 'Workspaces' },
  { id: 'ws-storage',               testid: 'ws-storage',               label: 'Storage',                     category: 'Workspaces' },
];

export function buttonLabel(id) {
  const m = DASHBOARD_BUTTONS.find((b) => b.id === id);
  return m ? m.label : id;
}
